// Building Safety Case (BSC) — record per HRB with section-completeness tracking (Phase 6)
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Building2, Plus, X, Save, Loader2, Trash2, Sparkles, FileText, BookText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { useAssignRecipients, AssignRecipientsStep } from "@/components/AssignRecipientsStep";

const CAN_EDIT = new Set(["director", "administrator", "consultant"]);
const STATUS_TONE = {
    Draft: "neutral", "In review": "amber", Submitted: "blue", Accepted: "green", Rejected: "red",
    "In progress": "amber", "Awaiting information": "amber", "Ready for review": "blue",
    "Under review": "purple", "Changes required": "red", Approved: "green", Issued: "green", Archived: "neutral",
};
const SECTION_TONE = { "Not started": "neutral", "In progress": "amber", Complete: "green" };
const SECTION_STATUSES = ["Not started", "In progress", "Complete"];
const BAND = {
    Green: { dot: "#3F7D3A", bg: "#E6F1E4" },
    Amber: { dot: "#C68A1C", bg: "#FBF1DD" },
    Red: { dot: "#B33A2E", bg: "#F8E3E0" },
};

const completeness = (sections) => !sections?.length ? 0 : Math.round(sections.filter((s) => s.status === "Complete").length * 100 / sections.length);

export default function BscPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [projects, setProjects] = useState([]);
    const [meta, setMeta] = useState({ statuses: [], default_sections: [] });
    const [statusFilter, setStatusFilter] = useState("");
    const [portalVis, setPortalVis] = useState("");
    const [criticalVal, setCriticalVal] = useState("");
    const [projectFilter, setProjectFilter] = useState("");
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [denied, setDenied] = useState(false);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const [params, setParams] = useSearchParams();
    const navigate = useNavigate();
    const canEdit = user && CAN_EDIT.has(user.role);
    const lockedClient = portalClientId && !isInternalView;
    const projectId = params.get("project") || "";
    const [projectInfo, setProjectInfo] = useState(null);

    useEffect(() => {
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
        api.get("/bsc/meta").then((r) => setMeta(r.data)).catch(() => {});
    }, []);
    useEffect(() => {
        if (!projectId) { setProjectInfo(null); return; }
        api.get(`/projects/${projectId}`).then((r) => setProjectInfo(r.data)).catch(() => setProjectInfo(null));
    }, [projectId]);

    const refresh = useCallback(async () => {
        try { const { data } = await api.get("/bsc", { params: projectId ? { project_id: projectId } : {} }); setRows(data); setDenied(false); }
        catch (e) { if (e.response?.status === 403) setDenied(true); }
    }, [projectId]);
    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (params.get("new") === "1" && canEdit) {
            setDialog({ open: true, mode: "create", initial: projectId ? { project_id: projectId, portal_visible: true } : null });
            params.delete("new"); setParams(params, { replace: true });
        }
    }, [params, canEdit, setParams, projectId]);

    const buildingMap = Object.fromEntries(buildings.map((b) => [b.id, b]));
    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const visibleRows = rows.filter((r) => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (projectFilter && r.project_id !== projectFilter) return false;
        if (portalVis === "yes" && !r.portal_visible) return false;
        if (portalVis === "no" && r.portal_visible) return false;
        if (criticalVal === "yes" && !r.critical) return false;
        if (criticalVal === "no" && r.critical) return false;
        return true;
    });

    async function remove(rec) {
        if (!window.confirm("Delete this BSC record?")) return;
        try { await api.delete(`/bsc/${rec.id}`); toast.success("BSC record deleted"); refresh(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    return (
        <div data-testid="bsc-page" className="space-y-5">
            {denied ? (
                <div data-testid="bsc-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                    <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to the Building Safety Case.</p>
                </div>
            ) : (
            <>
            {projectId && (
                <div data-testid="bsc-project-banner" className="flex items-center justify-between gap-3 flex-wrap rounded-[10px] border px-4 py-2.5" style={{ borderColor: "#E7C9A8", background: "#FBF1E6" }}>
                    <div className="text-[12px] font-semibold" style={{ color: "#8A5A1E" }}>Filtered to project: {projectInfo?.name || projectId}</div>
                    <button data-testid="bsc-back-to-project" onClick={() => navigate(`/app/projects/${projectId}`)}
                        className="inline-flex items-center gap-1 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-[7px] bg-white border" style={{ borderColor: "#E7C9A8", color: "#8A5A1E" }}>← Back to project</button>
                </div>
            )}
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Safety & Compliance</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2"><Building2 className="w-5 h-5" style={{ color: "var(--brand-accent, #C75A1B)" }} />Building Safety Case</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>{visibleRows.length}{visibleRows.length !== rows.length ? ` of ${rows.length}` : ""} safety case record(s)</p>
                </div>
                {canEdit && (
                    <div className="flex items-center gap-2">
                        <button data-testid="bsc-boilerplate-link" onClick={() => navigate("/app/bsc-boilerplate")}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                            <BookText className="w-3.5 h-3.5" /> Boilerplate library
                        </button>
                        <button data-testid="bsc-new-button" onClick={() => setDialog({ open: true, mode: "create", initial: projectId ? { project_id: projectId, portal_visible: true } : null })}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            <Plus className="w-3.5 h-3.5" /> New BSC record
                        </button>
                    </div>
                )}
            </div>

            {!lockedClient && (
                <div className="flex items-center gap-3 flex-wrap" data-testid="bsc-filter-bar">
                    <select data-testid="bsc-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">All statuses</option>
                        {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {!projectId && (
                        <select data-testid="bsc-project-filter" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                            <option value="">All projects</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    )}
                    <select data-testid="bsc-portalvis-filter" value={portalVis} onChange={(e) => setPortalVis(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">Any visibility</option>
                        <option value="yes">Portal-visible</option>
                        <option value="no">Internal only</option>
                    </select>
                    <select data-testid="bsc-critical-filter" value={criticalVal} onChange={(e) => setCriticalVal(e.target.value)} className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                        <option value="">Any criticality</option>
                        <option value="yes">Critical only</option>
                        <option value="no">Non-critical</option>
                    </select>
                </div>
            )}

            {visibleRows.length === 0 && (
                <div className="bg-white border rounded-[11px] p-10 text-center italic" style={{ borderColor: "#E5E0DA", color: "#9A968F" }}>
                    No Building Safety Case records yet.
                </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
                {visibleRows.map((rec) => {
                    const pct = completeness(rec.sections);
                    return (
                        <div key={rec.id} data-testid={`bsc-card-${rec.id}`} onClick={() => navigate(`/app/bsc/${rec.id}`)}
                            className="bg-white border rounded-[12px] p-5 shadow-brand-sm hover:shadow-brand transition-shadow cursor-pointer" style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-[10.5px] font-mono" style={{ color: "#9A968F" }}>{rec.reference}</div>
                                    <h3 className="text-[15px] font-bold text-[#1F1F1F] mt-0.5">{buildingMap[rec.building_id]?.name || "Building"}</h3>
                                    <div className="text-[11.5px]" style={{ color: "#6F6A64" }}>{clientMap[rec.client_id]?.name}</div>
                                </div>
                                <Pill tone={STATUS_TONE[rec.status] || "neutral"}>{rec.status}</Pill>
                            </div>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <button data-testid={`bsc-open-workspace-${rec.id}`}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/app/bsc/${rec.id}`); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                    <FileText className="w-3 h-3" /> Open report workspace
                                </button>
                                {canEdit && (
                                    <button data-testid={`bsc-quick-edit-${rec.id}`}
                                        onClick={(e) => { e.stopPropagation(); setDialog({ open: true, mode: "edit", initial: rec }); }}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F0EDE8] text-[#6F6A64] text-[11px] font-semibold hover:bg-[#E7E2DB]">
                                        Quick edit
                                    </button>
                                )}
                                <button data-testid={`bsc-ask-ai-${rec.id}`}
                                    onClick={(e) => { e.stopPropagation(); navigate(`/app/ai-assist?context_type=bsc&context_id=${rec.id}&context_label=${encodeURIComponent("BSC — " + (buildingMap[rec.building_id]?.name || ""))}`); }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EEF3FB] text-[#1F6FE5] text-[11px] font-semibold hover:bg-[#E0ECFB]">
                                    <Sparkles className="w-3 h-3" /> Ask Beyond AI
                                </button>
                            </div>
                            <div className="mt-3">
                                {rec.readiness?.initialised ? (() => {
                                    const r = rec.readiness;
                                    const band = BAND[r.band] || BAND.Red;
                                    return (
                                        <div data-testid={`bsc-readiness-${rec.id}`}>
                                            <div className="flex items-center justify-between text-[11px] mb-1">
                                                <span className="inline-flex items-center gap-1.5" style={{ color: "#6F6A64" }}>
                                                    <span className="w-2 h-2 rounded-full" style={{ background: band.dot }} />
                                                    BSCR readiness
                                                    <span data-testid={`bsc-readiness-band-${rec.id}`} className="px-1.5 py-px rounded-full text-[10px] font-bold" style={{ background: band.bg, color: band.dot }}>{r.band}</span>
                                                </span>
                                                <span className="font-semibold" style={{ color: band.dot }}>{r.score}%</span>
                                            </div>
                                            <div className="h-2 rounded-full bg-[#F0EDE8] overflow-hidden">
                                                <div className="h-full rounded-full transition-all" style={{ width: `${r.score}%`, background: band.dot }} />
                                            </div>
                                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10.5px]" style={{ color: "#6F6A64" }}>
                                                <span><b>{r.section_completion_pct}%</b> approved</span>
                                                <span><b>{r.field_fill_pct}%</b> fields</span>
                                                <span className={r.missing_open ? "text-[#C68A1C] font-semibold" : ""}><b>{r.missing_open}</b> missing</span>
                                                <span className={r.overdue_actions ? "text-[#B33A2E] font-semibold" : ""}><b>{r.overdue_actions}</b> overdue</span>
                                            </div>
                                        </div>
                                    );
                                })() : (
                                    <div>
                                        <div className="flex items-center justify-between text-[11px] mb-1" style={{ color: "#6F6A64" }}>
                                            <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#C2BCB4]" />BSCR readiness <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-[#F0EDE8] text-[#9A968F]">Not started</span></span>
                                            <span className="font-semibold">{pct}%</span>
                                        </div>
                                        <div className="h-2 rounded-full bg-[#F0EDE8] overflow-hidden">
                                            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: "var(--brand-accent, #C75A1B)" }} />
                                        </div>
                                        <div className="mt-1 text-[10.5px] italic" style={{ color: "#9A968F" }}>Open the workspace to build &amp; pre-fill this report.</div>
                                    </div>
                                )}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-3 text-[11px]" style={{ color: "#6F6A64" }}>
                                {rec.accountable_person && <span><b>PAP:</b> {rec.accountable_person}</span>}
                                {rec.bsr_registration && <span><b>BSR:</b> {rec.bsr_registration}</span>}
                                {rec.next_review_date && <span><b>Review:</b> {rec.next_review_date}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {dialog.open && (
                <BscDialog mode={dialog.mode} initial={dialog.initial} meta={meta} clients={clients} buildings={buildings}
                    lockedClientId={lockedClient ? portalClientId : null}
                    onClose={() => setDialog((d) => ({ ...d, open: false }))}
                    onSaved={(created) => { refresh(); if (created?.id) navigate(`/app/bsc/${created.id}`); }} />
            )}
            </>
            )}
        </div>
    );
}

function BscDialog({ mode, initial, meta, clients, buildings, lockedClientId, onClose, onSaved }) {
    const [form, setForm] = useState({});
    const [sections, setSections] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        setForm({
            client_id: lockedClientId || "", building_id: "", title: "", status: "Draft",
            accountable_person: "", bsr_registration: "", assessment_date: "", next_review_date: "", summary: "",
            ...(initial || {}),
        });
        setSections(initial?.sections ? initial.sections.map((s) => ({ ...s })) : []);
    }, [initial, lockedClientId]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const assign = useAssignRecipients(form.client_id);
    const filteredBuildings = buildings.filter((b) => !form.client_id || b.client_id === form.client_id);
    const pct = completeness(sections);

    async function save() {
        if (mode === "create" && (!form.client_id || !form.building_id)) { toast.error("Client and building are required"); return; }
        setBusy(true);
        try {
            if (mode === "create") {
                const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v != null));
                if (assign.recipients.length) payload.assigned_recipients = assign.recipients;
                if (assign.message.trim()) payload.assigned_message = assign.message.trim();
                const res = await api.post("/bsc", payload);
                toast.success("BSC record created");
                onSaved?.(res.data); onClose?.();
                return;
            } else {
                const payload = {
                    title: form.title, status: form.status, accountable_person: form.accountable_person,
                    bsr_registration: form.bsr_registration, assessment_date: form.assessment_date,
                    submitted_date: form.submitted_date, accepted_date: form.accepted_date,
                    next_review_date: form.next_review_date, summary: form.summary, sections,
                };
                await api.patch(`/bsc/${initial.id}`, payload);
                toast.success("BSC record updated");
            }
            onSaved?.(); onClose?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        finally { setBusy(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="bsc-form-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[680px] max-h-[92vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h2 className="font-display text-[18px] font-bold text-[#1F1F1F]">{mode === "create" ? "New Building Safety Case" : `BSC ${initial?.reference}`}</h2>
                    <button data-testid="bsc-form-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-3.5 form-pane">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <BField label="Client" required>
                            <select data-testid="bsc-form-client" value={form.client_id || ""} disabled={mode === "edit" || !!lockedClientId} onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">Select…</option>{clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </BField>
                        <BField label="Building" required>
                            <select data-testid="bsc-form-building" value={form.building_id || ""} disabled={mode === "edit"} onChange={(e) => set("building_id", e.target.value)} className="fi">
                                <option value="">Select…</option>{filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </BField>
                    </div>
                    <BField label="Title"><input data-testid="bsc-form-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="fi" /></BField>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <BField label="Status">
                            <select data-testid="bsc-form-status" value={form.status || "Draft"} onChange={(e) => set("status", e.target.value)} className="fi">
                                {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </BField>
                        <BField label="Accountable person (PAP)"><input data-testid="bsc-form-pap" value={form.accountable_person || ""} onChange={(e) => set("accountable_person", e.target.value)} className="fi" /></BField>
                        <BField label="BSR registration"><input data-testid="bsc-form-bsr" value={form.bsr_registration || ""} onChange={(e) => set("bsr_registration", e.target.value)} className="fi" /></BField>
                        <BField label="Assessment date"><input data-testid="bsc-form-assessment" type="date" value={form.assessment_date || ""} onChange={(e) => set("assessment_date", e.target.value)} className="fi" /></BField>
                        <BField label="Next review date"><input data-testid="bsc-form-review" type="date" value={form.next_review_date || ""} onChange={(e) => set("next_review_date", e.target.value)} className="fi" /></BField>
                    </div>
                    <BField label="Summary"><textarea data-testid="bsc-form-summary" rows={2} value={form.summary || ""} onChange={(e) => set("summary", e.target.value)} className="fi" /></BField>

                    {mode === "create" && <AssignRecipientsStep state={assign} testIdPrefix="bsc-form-assign" />}

                    {mode === "edit" && sections.length > 0 && (
                        <div className="border rounded-[10px] p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[12px] font-bold text-[#1F1F1F]">Safety case sections</span>
                                <span className="text-[11px] font-semibold" style={{ color: "var(--brand-accent, #C75A1B)" }}>{pct}% complete</span>
                            </div>
                            <div className="space-y-1.5">
                                {sections.map((s, i) => (
                                    <div key={s.key || s.title || i} data-testid={`bsc-section-${i}`} className="flex items-center gap-2">
                                        <span className="flex-1 text-[12px] text-[#2D2D2D]">{s.title}</span>
                                        <select data-testid={`bsc-section-status-${i}`} value={s.status}
                                            onChange={(e) => setSections((arr) => arr.map((x, idx) => idx === i ? { ...x, status: e.target.value } : x))}
                                            className="text-[11px] font-semibold border rounded-[6px] px-2 py-1 bg-white" style={{ borderColor: "#E5E0DA" }}>
                                            {SECTION_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid="bsc-form-cancel" className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="bsc-form-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}{busy ? "Saving…" : "Save"}
                    </button>
                </div>
                <style>{`
                    .form-pane .fi { width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA; border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none; }
                    .form-pane .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}

function BField({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
